import i18n from 'i18next'

import { type ProductWrapper } from '@state/product.ts'
import { type TransformedTodoListResponse } from '@state/todoList'
import {
  type ACTION_TYPE,
  type TaskItemResponse,
  type TransformedTaskItemResponse
} from '@state/todoList'

export interface FilterAndSort {
  completed: boolean
  primaryFilter: Exclude<ACTION_TYPE, 'NONE'>
  secondaryFilter: 'NONE' | 'NEW' | 'ADVERTISEMENT' | 'MANUAL_ORDER'
  sort: 'NEW' | 'NEW-' | 'ADVERTISEMENT' | 'ALPHA' | 'SHELF'
}

const SEPARATOR = '|'

export class FilterAndSortWrapper implements FilterAndSort {
  completed: boolean
  primaryFilter: FilterAndSort['primaryFilter']
  secondaryFilter: FilterAndSort['secondaryFilter']
  sort: FilterAndSort['sort']

  static readonly DEFAULTS: FilterAndSort = {
    completed: false,
    primaryFilter: 'CHECK',
    secondaryFilter: 'NONE',
    sort: 'ALPHA',
  }

  constructor(source: Partial<FilterAndSort> = {}) {
    const defaults = FilterAndSortWrapper.DEFAULTS

    this.completed = source.completed ?? defaults.completed
    this.primaryFilter = source.primaryFilter ?? defaults.primaryFilter
    this.secondaryFilter = source.secondaryFilter ?? defaults.secondaryFilter
    this.sort = source.sort ?? defaults.sort
  }

  toString(): string {
    return `${this.completed ? 1 : 0}${SEPARATOR}${this.primaryFilter}${SEPARATOR}${this.secondaryFilter}${SEPARATOR}${this.sort}`
  }

  static parse(serialized: string): FilterAndSortWrapper {
    const parts = serialized.split(SEPARATOR)

    return new FilterAndSortWrapper({
      completed: parts[0] === '1',
      primaryFilter: parts[1] as FilterAndSort['primaryFilter'],
      secondaryFilter: parts[2] as FilterAndSort['secondaryFilter'],
      sort: parts[3] as FilterAndSort['sort'],
    })
  }
}

export class TodoItemWrapper implements TransformedTaskItemResponse {
  id: string
  shelfId: string
  stockLevel: TransformedTaskItemResponse['stockLevel']
  product: ProductWrapper
  action: TransformedTaskItemResponse['action']
  isNew: boolean
  status: TransformedTaskItemResponse['status']

  constructor(item: Omit<TaskItemResponse, 'product'> & {product: ProductWrapper}) {
    this.id = item.id
    this.shelfId = item.shelfId
    this.stockLevel = item.stockLevel
    this.product = item.product
    this.action = item.action
    this.isNew = item.isNew
    this.status = item.status
  }

  isCompleted(): boolean {
    return this.status === 'COMPLETED'
  }
}

export function filterList(todoList: TransformedTodoListResponse, filterAndSort: FilterAndSortWrapper) {
  const { primaryFilter, secondaryFilter } = filterAndSort

  if (filterAndSort.completed) {
    // Completed mode: return only completed tasks, ignore other filters
    return todoList.filter(item => item.isCompleted())
  }

  // Not completed mode: start by excluding completed tasks
  const uncompleted = todoList.filter(item => !item.isCompleted())

  // Primary filter (by task action type)
  const afterPrimary = uncompleted.filter(item => item.action === primaryFilter)

  // Secondary filter (currently only NEW supported)
  if (secondaryFilter === 'NEW') {
    return afterPrimary.filter(item => item.isNew)
  }

  return afterPrimary
}

// Broader alphabetic detection using any Unicode letter; fallback to Latin extended if needed.
const alphaRegex = /\p{L}/u

export function sortTodoList(list: TransformedTodoListResponse, filterAndSort: FilterAndSortWrapper): TransformedTodoListResponse {
  const { sort } = filterAndSort
  const isNewSort = sort === 'NEW' || sort === 'NEW-'
  const isShelfSort = sort === 'SHELF'
  const collator = new Intl.Collator(i18n.language, { numeric: true, sensitivity: 'base' })

  // Decorate step: compute sortable keys once per item
  const decorated = list.map(item => {
    const isNew = item.isNew
    const hasAlpha = alphaRegex.test(item.product.name)

    // Priority groups (lower number comes first)
    const newPriority = isNewSort ? (sort === 'NEW' ? (isNew ? 0 : 1) : (isNew ? 1 : 0)) : 0
    const alphaPriority = hasAlpha ? 0 : 1
    const shelfPriority = isShelfSort ? (item.shelfId || '') : ''

    return {
      item,
      newPriority,
      alphaPriority,
      shelfPriority,
      name: item.product.name,
    }
  })

  decorated.sort((a, b) => {
    if (a.newPriority !== b.newPriority) return a.newPriority - b.newPriority
    if (a.shelfPriority !== b.shelfPriority) return collator.compare(a.shelfPriority, b.shelfPriority)
    if (a.alphaPriority !== b.alphaPriority) return a.alphaPriority - b.alphaPriority
    return collator.compare(a.name, b.name)
  })

  // Undecorate step
  return decorated.map(d => d.item)
}